import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/home.css";
import { API_BASE } from "../config";
import useCountUp from "../hooks/useCountUp";
import trackPageView from "../utils/trackPageView";
import { FiDownload, FiUpload, FiTruck } from "react-icons/fi";

const isVideoUrl = (url) => url && /\.(mp4|webm|mov)(\?|$)/i.test(url);

function CounterItem({ label, value, suffix }) {
  const { count, ref } = useCountUp(value);
  return (
    <div className="stat" ref={ref}>
      <h2>{count}{suffix}</h2>
      <p>{label}</p>
    </div>
  );
}

function Home() {
  const [counters, setCounters] = useState([]);
  const [heroImage, setHeroImage] = useState("");
  const [aboutImage, setAboutImage] = useState("");

  useEffect(() => {
    trackPageView("home");

    fetch(`${API_BASE}/settings`)
      .then((res) => res.json())
      .then((data) => {
        if (data.counters) setCounters(data.counters);
        if (data.heroImage) setHeroImage(data.heroImage);
        if (data.aboutImage) setAboutImage(data.aboutImage);
      })
      .catch(() => {});
  }, []);

  return (
    <main>
      {/* Section 1 - Hero */}
      <section className="hero">
        {heroImage && (
          <>
            {isVideoUrl(heroImage) ? (
              <video
                className="hero-bg hero-bg-video"
                src={heroImage}
                autoPlay
                loop
                muted
                playsInline
                aria-label="KrivaExim global trade"
              />
            ) : (
              <div
                className="hero-bg"
                style={{ backgroundImage: `url(${heroImage})` }}
                role="img"
                aria-label="KrivaExim global trade"
              />
            )}
            <div className="hero-bg-overlay" />
          </>
        )}
        <div className="hero-text">
          <h1>Global Trade, Simplified.</h1>
          <p>
            We help businesses import and export goods across 40+ countries
            with reliable logistics, transparent pricing, and dedicated
            support at every step.
          </p>
          <Link to="/products" className="btn-primary">Browse Products</Link>
          <Link to="/contact" className="btn-secondary" style={{color: "white"}}>Contact Us</Link>
        </div>
        {!heroImage && <div className="hero-visual"></div>}
      </section>

      {/* Section 2 - Counters (animated, NOT parallax) */}
      <section className="stats">
        {counters.length > 0
          ? counters.map((c, i) => <CounterItem key={i} {...c} />)
          : <>
              <div className="stat"><h2>25+</h2><p>Years of Experience</p></div>
              <div className="stat"><h2>40+</h2><p>Countries Served</p></div>
              <div className="stat"><h2>1,200+</h2><p>Shipments Delivered</p></div>
              <div className="stat"><h2>98%</h2><p>Client Satisfaction</p></div>
            </>
        }
      </section>

      {/* Section 3 - About preview */}
      <section className="about-preview">
        <div className="about-preview-inner">
          <div className="about-preview-text">
            <span className="section-tag">Who We Are</span>
            <h2>Building Bridges Across Borders</h2>
            <p>
              KrivaExim started as a small trading desk and has grown into a
              full-service import-export partner working with clients across
              40+ countries. We specialize in sourcing quality raw materials,
              machinery, and finished goods.
            </p>
            <Link to="/about" className="btn-primary">Learn More</Link>
          </div>
          {aboutImage && isVideoUrl(aboutImage) ? (
            <div className="about-preview-image has-image">
              <video
                src={aboutImage}
                autoPlay
                loop
                muted
                playsInline
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }}
                aria-label="About KrivaExim"
              />
            </div>
          ) : (
            <div
              className={`about-preview-image${aboutImage ? " has-image" : ""}`}
              style={aboutImage ? { backgroundImage: `url(${aboutImage})` } : undefined}
              role={aboutImage ? "img" : undefined}
              aria-label={aboutImage ? "About KrivaExim" : undefined}
            ></div>
          )}
        </div>
      </section>

      {/* Section 4 - Parallax CTA */}
      <section className="parallax-section parallax-cta">
        <div className="parallax-overlay"></div>
        <div className="parallax-content">
          <h2>Ready to Go Global?</h2>
          <p>Partner with us for seamless import and export solutions tailored to your business.</p>
          <Link to="/contact" className="btn-primary">Get a Quote</Link>
        </div>
      </section>

      {/* Section 5 - Services */}
      <section className="services">
        <span className="section-tag center">What We Do</span>
        <h2>End-to-End Trade Solutions</h2>
        <p className="services-sub">Built around your business needs.</p>
        <div className="cards">
          <div className="card">
            <div className="card-icon"><FiDownload size={32} color="#0f5132" /></div>
            <h3>Import Services</h3>
            <p>Sourcing and customs handling for raw materials, machinery, and electronics from global suppliers.</p>
          </div>
          <div className="card">
            <div className="card-icon"><FiUpload size={32} color="#0f5132" /></div>
            <h3>Export Services</h3>
            <p>Reliable shipping of textiles, agricultural goods, and industrial products to markets worldwide.</p>
          </div>
          <div className="card">
            <div className="card-icon"><FiTruck size={32} color="#0f5132" /></div>
            <h3>Logistics &amp; Freight</h3>
            <p>Door-to-door freight management with real-time shipment tracking and delivery updates.</p>
          </div>
        </div>
      </section>

      {/* Section 6 - Parallax Why Choose Us */}
      <section className="parallax-section parallax-why">
        <div className="parallax-overlay"></div>
        <div className="parallax-content">
          <h2>Why Choose KrivaExim?</h2>
          <div className="why-grid">
            <div className="why-item">
              <h3>25+ Years</h3>
              <p>Combined industry experience in global trade</p>
            </div>
            <div className="why-item">
              <h3>Verified Suppliers</h3>
              <p>Quality-checked sourcing from trusted partners</p>
            </div>
            <div className="why-item">
              <h3>End-to-End</h3>
              <p>Complete customs and freight handling</p>
            </div>
            <div className="why-item">
              <h3>Dedicated Support</h3>
              <p>From enquiry to delivery, every step covered</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 - Testimonials */}
      <section className="testimonials">
        <span className="section-tag center">Testimonials</span>
        <h2>What Our Clients Say</h2>
        <div className="testimonial-grid">
          <div className="testimonial-card">
            <p>"KrivaExim handled our entire textile export to Europe flawlessly. Reliable and transparent."</p>
            <span>- Rajesh Kumar, Textile Manufacturer</span>
          </div>
          <div className="testimonial-card">
            <p>"Best logistics partner we've had. Real-time tracking and on-time delivery every time."</p>
            <span>- Sarah Mitchell, Trading Co.</span>
          </div>
          <div className="testimonial-card">
            <p>"Their sourcing quality is top-notch. We've been working with them for 3+ years now."</p>
            <span>- Ahmed Al-Rashid, Gulf Imports</span>
          </div>
        </div>
      </section>

      {/* Section 8 - Parallax Final CTA */}
      <section className="parallax-section parallax-final">
        <div className="parallax-overlay"></div>
        <div className="parallax-content">
          <h2>Let's Trade Together</h2>
          <p>Whether you're importing or exporting, we make global trade simple.</p>
          <Link to="/contact" className="btn-primary">Contact Us Today</Link>
        </div>
      </section>
    </main>
  );
}

export default Home;
